// Ported from eb-ui `src/legacy/components/FollowOrganizerButton/FollowOrganizerButton.tsx`.
// The legacy `addFollow`/`withFollow` redux HOCs (redux-destination owned
// follow-state + imperative ReactDOM.render) are GONE. Rewritten on the D4
// `@eventbrite/follow-organizer-ebui` client hooks:
//   - `useFollowStatus([organizerId], { initialData, enabled: false })` seeds
//     the follow state from the SSR-known `collection.organizer.follow_status`
//     so there is NO client status fetch + no skeleton flash.
//   - `useFollowMutation({ organizerIds: [organizerId] })` for the follow /
//     unfollow click — it POSTs through the app's own BFF
//     (`/cc/api/v3/...`); the `FollowClientProvider` in `_app` is already
//     wired with a CSRF-echoing fetcher, so we only call the hooks here.
//
// The SAME public exports are preserved so `OrganizerPanel` / `FollowOrganizer`
// call-sites are unchanged: `FollowOrganizerButton`, `FollowOrganizer`,
// `FollowButtonProps` (+ the `FollowButtonStyle` enum).
import {
    FollowButton,
    SOLITARY_BUTTON_STYLE,
    useFollowMutation,
    useFollowStatus,
} from '@eventbrite/follow-organizer-ebui';
import React, { useContext } from 'react';

import { ApplicationContext } from '../../context';
import { AnalyticsCategory } from '../../_shared';
import { gettext } from '@/lib/gettext';
import type { CollectionServerData } from '@/types';

enum FollowButtonStyle {
    STANDARD_BUTTON_STYLE,
    SOLITARY_BUTTON_STYLE,
}

export interface FollowButtonProps {
    isAuthenticated: boolean;
    gaSettings: any;
    gaCategory: AnalyticsCategory;
    organizers: string[];
    isFollowing: boolean;
    organizerId: string;
    style?: FollowButtonStyle;
    text?: { active: string; default: string };
}

// BUGBOT (Medium): labels are built LAZILY per render — a module-level `gettext`
// call would freeze English in the eager string shim (the module loads before
// `_app` runs `setMessages`).
//
// VISUAL FIX: the organizer-panel follow button used the "organizer" suffix
// ("Follow organizer"), which made it wider than the Contact button next to it
// and mismatched the live page. The vendored `FollowButton` package default is
// just "Follow"/"Following" — match it. (The `FollowOrganizer` wrapper used by
// the empty-state keeps its explicit "Follow organizer" text, per eb-ui.)
const getDefaultText = (): { active: string; default: string } => ({
    active: gettext('Following').toString(),
    default: gettext('Follow').toString(),
});

const FollowOrganizerButtonBase: React.FunctionComponent<FollowButtonProps> = (
    props,
) => {
    const { organizerId, isFollowing, isAuthenticated, text } = props;
    const appContext = useContext(ApplicationContext) as
        | CollectionServerData
        | null;
    const organizer = appContext?.collection?.organizer;

    const organizerName = organizer?.name;
    const seedFollowedByYou = organizer?.follow_status?.followed_by_you ?? isFollowing;
    const seedNumFollowers = organizer?.follow_status?.num_followers;

    // Seed from the SSR-known follow status: no client fetch, no skeleton.
    const followStatus = useFollowStatus([organizerId], {
        initialData: {
            [organizerId]: {
                id: organizerId,
                name: organizerName || '',
                url: organizer?.url || '',
                followedByYou: seedFollowedByYou,
                numFollowers: seedNumFollowers,
            },
        },
        enabled: false,
    });
    const { follow, unfollow } = useFollowMutation({
        organizerIds: [organizerId],
    });

    const status = followStatus.data?.[organizerId];
    const isFollowingState: boolean | null =
        status?.followedByYou ?? seedFollowedByYou;

    // BUGBOT (Medium): legacy redux HOCs gated follow/unfollow for guests —
    // `addFollow._handleOnFollow` checked auth, and for a logged-out viewer it
    // rendered the login affordance and NEVER fired the mutation. The D4
    // rewrite accepted `isAuthenticated` but ignored it, so a guest click hit
    // a doomed (unauthenticated) BFF/Core mutation. Reproduce the gate: if
    // logged-out, follow attempts route to `/signin/?referrer=…&stld=1`
    // (follow-organizer-ebui `addFollow.jsx:365`) and never call `follow`.
    const requireLogin = () => {
        if (isAuthenticated === false && typeof window !== 'undefined') {
            const referrer = window.location.pathname + window.location.search;
            const query = `?referrer=${encodeURIComponent(referrer)}&stld=1`;
            window.location.assign(`/signin/${query}`);
            return true;
        }
        return false;
    };

    const handleFollow = () => {
        if (!requireLogin()) {
            follow(organizerId);
        }
    };

    const handleUnFollow = () => {
        // BUGBOT (Medium): apply the same sign-in gate to UNfollow — a
        // logged-out viewer can still hold a stale `followedByYou` seed (SSR
        // or a race) and would otherwise fire an unauthenticated unfollow
        // mutation. The legacy HOCs gated both actions.
        if (!requireLogin()) {
            unfollow(organizerId);
        }
    };

    return (
        <FollowButton
            isFollowing={isFollowingState}
            organizerName={organizerName}
            numFollowers={status?.numFollowers ?? seedNumFollowers}
            style={SOLITARY_BUTTON_STYLE}
            text={text ?? getDefaultText()}
            onFollow={handleFollow}
            onUnFollow={handleUnFollow}
        />
    );
};

export const FollowOrganizerButton = FollowOrganizerButtonBase;

export const FollowOrganizer = () => (
    <ApplicationContext.Consumer>
        {(context) => {
            const data = context as CollectionServerData | null;
            return (
                <FollowOrganizerButton
                    isAuthenticated={context?.user?.isAuthenticated || false}
                    gaSettings={context?.gaSettings}
                    gaCategory={AnalyticsCategory.Listing}
                    organizers={
                        data?.collection
                            ? [data.collection?.organizer.id]
                            : []
                    }
                    organizerId={data?.collection?.organizer.id || ''}
                    isFollowing={
                        data?.collection?.organizer.follow_status
                            ?.followed_by_you || false
                    }
                    text={{
                        active: gettext('Following organizer').toString(),
                        default: gettext('Follow organizer').toString(),
                    }}
                />
            );
        }}
    </ApplicationContext.Consumer>
);
